import React from "react";
import { createPortal } from "react-dom";
import {
  X,
  Building2,
  Phone,
  Mail,
  MapPin,
  User,
  StickyNote,
  Layers,
  Pencil,
  CalendarRange,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";

const AFFILIATION_LABEL = {
  hq: "Trụ sở / HQ",
  affiliated: "Trực thuộc",
  franchise: "Nhượng quyền",
};

const display = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "—" : v;

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const monthShort = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${monthShort}-${year}`;
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="space-y-3 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:space-y-4 sm:p-5">
    <div className="flex items-center gap-2.5">
      <div className="rounded-xl border border-red-700/10 bg-red-700/5 p-2 text-red-700">
        <Icon className="h-4 w-4" strokeWidth={2.25} />
      </div>
      <h3 className="text-sm font-black uppercase tracking-wide text-gray-800">
        {title}
      </h3>
    </div>
    {children}
  </section>
);

const Row = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
      {label}
    </span>
    <div className="text-sm font-semibold text-gray-800 break-words">
      {children}
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
      isActive
        ? "border-emerald-100 bg-emerald-50 text-emerald-600"
        : "border-slate-200 bg-slate-100 text-slate-400"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        isActive ? "animate-pulse bg-emerald-500" : "bg-slate-300"
      }`}
    />
    {isActive ? "Hoạt động" : "Ngừng nhận"}
  </span>
);

const AffiliationBadge = ({ type, label }) => {
  const tone =
    type === "hq"
      ? "border-red-100 bg-red-50 text-red-700"
      : type === "franchise"
        ? "border-amber-100 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${tone}`}
    >
      <Layers size={11} />
      {label}
    </span>
  );
};

const BranchDetailModal = ({ isOpen, branch, onClose, onEdit }) => {
  if (typeof document === "undefined") return null;
  if (!isOpen || !branch) return null;

  const canEdit = hasPermission("hrm.branches", "U");
  const aff = branch.affiliation_type;
  const affLabel =
    aff && AFFILIATION_LABEL[aff] ? AFFILIATION_LABEL[aff] : display(aff);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Building2
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      strokeWidth={2.25}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      {branch.name}
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Chi tiết chi nhánh
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StatusBadge isActive={!!branch.is_active} />
                      <AffiliationBadge type={aff} label={affLabel} />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain bg-slate-50/40 px-4 py-4 sm:space-y-4 sm:px-6 sm:py-5">
                <Section icon={Building2} title="Thông tin chi nhánh">
                  <Row label="Địa chỉ">
                    <span className="flex items-start gap-2 font-medium text-gray-700">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                      {display(branch.address)}
                    </span>
                  </Row>
                  {aff === "franchise" ? (
                    <div className="grid grid-cols-2 gap-3 rounded-xl border border-amber-100 bg-amber-50/50 p-3">
                      <Row label="NQ từ ngày">
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarRange className="h-3.5 w-3.5 text-amber-600" />
                          {formatDate(branch.franchise_start_date)}
                        </span>
                      </Row>
                      <Row label="NQ đến ngày">
                        {formatDate(branch.franchise_end_date)}
                      </Row>
                    </div>
                  ) : null}
                </Section>

                <Section icon={Mail} title="Liên hệ">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Row label="Email">
                      <span className="flex items-start gap-2 break-all">
                        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                        {display(branch.email)}
                      </span>
                    </Row>
                    <Row label="Hotline">
                      <span className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                        {display(branch.phone)}
                      </span>
                    </Row>
                  </div>
                </Section>

                <Section icon={User} title="Người đại diện">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Row label="Họ và tên">{display(branch.manager)}</Row>
                    <Row label="Điện thoại">
                      <span className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                        {display(branch.manager_phone)}
                      </span>
                    </Row>
                  </div>
                </Section>

                <Section icon={StickyNote} title="Ghi chú và trạng thái">
                  <Row label="Ghi chú">
                    <p className="whitespace-pre-wrap font-normal leading-relaxed text-gray-700">
                      {display(branch.note)}
                    </p>
                  </Row>
                  <div className="grid grid-cols-2 gap-3">
                    <Row label="Trạng thái">
                      <StatusBadge isActive={!!branch.is_active} />
                    </Row>
                    <Row label="Cập nhật lần cuối">
                      {formatDate(branch.last_changed)}
                    </Row>
                  </div>
                </Section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-4 sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-full border border-gray-200 bg-white px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 sm:flex-none sm:px-6 sm:py-2.5"
                >
                  Đóng
                </button>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => onEdit?.(branch)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-4 py-3 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg sm:flex-none sm:px-6 sm:py-2.5"
                  >
                    <Pencil size={16} strokeWidth={2.25} />
                    Sửa
                  </button>
                ) : null}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BranchDetailModal;
